import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Input, InputFooter, InputWrapper } from '@amzn/stencil-react-components/input'
import { Col } from '@amzn/stencil-react-components/layout'
import { Select } from '@amzn/stencil-react-components/select'
import { Text } from '@amzn/stencil-react-components/text'
import { TextArea } from '@amzn/stencil-react-components/text-area'

import { ModalContent } from 'src/components/ModalContent'
import {
    CANDIDATE_PERSONAS,
    CandidatePersona,
    CUSTOM_PERSONA_ID,
    MAX_PROMPT_LENGTH,
} from 'src/services/virtual-candidate/personas'
import {
    jobRoleProblem,
    requisitionProblem,
    VirtualCandidateJob,
} from 'src/services/virtual-candidate/sessionRequest'

/** The persona a run starts with: a preset, or a prompt written in the picker. */
export interface PersonaChoice {
    id: string
    name: string
    prompt: string
}

export interface VirtualCandidateSetupViewProps {
    choice: PersonaChoice
    onChoiceChange: (choice: PersonaChoice) => void
    job: VirtualCandidateJob
    onJobChange: (job: VirtualCandidateJob) => void
    onStart: () => void
    onCancel: () => void
}

const CUSTOM: CandidatePersona = {
    id: CUSTOM_PERSONA_ID,
    name: 'Custom',
    description: 'Write your own instructions for the candidate.',
    prompt: '',
}

const OPTIONS: readonly CandidatePersona[] = [...CANDIDATE_PERSONAS, CUSTOM]

export const choiceFor = (persona: CandidatePersona): PersonaChoice => ({
    id: persona.id,
    name: persona.name,
    prompt: persona.prompt,
})

/** Why a custom prompt can't be used yet, or undefined when it can. */
export const promptProblem = (prompt: string): string | undefined => {
    if (!prompt.trim()) return 'Describe the candidate before starting.'
    if (prompt.length > MAX_PROMPT_LENGTH) {
        return `Keep the instructions under ${MAX_PROMPT_LENGTH.toLocaleString()} characters.`
    }
    return undefined
}

/**
 * The modal's first step: the job to interview for and who the AI candidate is, then start the
 * interview. Delivery picks the competencies from the requisition through CIS.
 */
export const VirtualCandidateSetupView = ({
    choice,
    onChoiceChange,
    job,
    onJobChange,
    onStart,
    onCancel,
}: VirtualCandidateSetupViewProps) => {
    const selected = OPTIONS.find((option) => option.id === choice.id) ?? OPTIONS[0]
    const isCustom = selected.id === CUSTOM_PERSONA_ID
    const requisitionError = requisitionProblem(job.requisitionId)
    const roleError = jobRoleProblem(job.jobRole)
    const problem =
        requisitionError ?? roleError ?? (isCustom ? promptProblem(choice.prompt) : undefined)

    return (
        <ModalContent
            titleText='Virtual candidate interview'
            maxWidth='640px'
            buttons={[
                <Button key='cancel' dataTestId='virtual-candidate-cancel' onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key='start'
                    dataTestId='virtual-candidate-start'
                    variant={ButtonVariant.Primary}
                    aria-disabled={Boolean(problem)}
                    onClick={() => {
                        if (!problem) onStart()
                    }}
                >
                    Start interview
                </Button>,
            ]}
        >
            <Col gridGap='S400'>
                <Text>
                    Delivery orders this module&apos;s last saved draft for the requisition and
                    picks its competencies. Choose how the AI candidate behaves, then start the
                    interview with Irene.
                </Text>
                <InputWrapper
                    id='virtual-candidate-requisition'
                    labelText='Requisition ID'
                    required
                    error={Boolean(requisitionError)}
                    footer={(footerProps) => (
                        <InputFooter
                            {...footerProps}
                            dataTestId='virtual-candidate-requisition-footer'
                        >
                            {requisitionError ??
                                'CIS picks the competencies from this requisition.'}
                        </InputFooter>
                    )}
                >
                    {(inputProps) => (
                        <Input
                            {...inputProps}
                            dataTestId='virtual-candidate-requisition'
                            value={job.requisitionId}
                            onChange={(event) =>
                                onJobChange({ ...job, requisitionId: event.target.value })
                            }
                        />
                    )}
                </InputWrapper>
                <InputWrapper
                    id='virtual-candidate-job-role'
                    labelText='Job role (optional)'
                    error={Boolean(roleError)}
                    footer={(footerProps) => (
                        <InputFooter
                            {...footerProps}
                            dataTestId='virtual-candidate-job-role-footer'
                        >
                            {roleError ??
                                "Irene names this role in her questions. Blank uses Delivery's default."}
                        </InputFooter>
                    )}
                >
                    {(inputProps) => (
                        <Input
                            {...inputProps}
                            dataTestId='virtual-candidate-job-role'
                            value={job.jobRole}
                            onChange={(event) =>
                                onJobChange({ ...job, jobRole: event.target.value })
                            }
                        />
                    )}
                </InputWrapper>
                <InputWrapper id='virtual-candidate-persona' labelText='Candidate'>
                    {(inputProps) => (
                        <Select
                            {...inputProps}
                            dataTestId='virtual-candidate-persona'
                            options={[...OPTIONS]}
                            renderOption={(option: CandidatePersona) => option.name}
                            valueAccessor={(option: CandidatePersona) => option.id}
                            value={selected}
                            onChange={(option: CandidatePersona) => {
                                // Custom starts from the persona chosen before it, ready to edit.
                                onChoiceChange(
                                    option.id === CUSTOM_PERSONA_ID
                                        ? { ...choice, id: CUSTOM.id, name: CUSTOM.name }
                                        : choiceFor(option)
                                )
                            }}
                        />
                    )}
                </InputWrapper>
                <Text color='neutral70' dataTestId='virtual-candidate-persona-description'>
                    {selected.description}
                </Text>
                {isCustom && (
                    <InputWrapper
                        id='virtual-candidate-prompt'
                        labelText='Instructions for the candidate'
                        error={Boolean(promptProblem(choice.prompt))}
                        footer={(footerProps) => (
                            <InputFooter
                                {...footerProps}
                                dataTestId='virtual-candidate-prompt-footer'
                            >
                                {promptProblem(choice.prompt)
                                    ? promptProblem(choice.prompt)
                                    : `${choice.prompt.length.toLocaleString()} / ${MAX_PROMPT_LENGTH.toLocaleString()} characters`}
                            </InputFooter>
                        )}
                    >
                        {(inputProps) => (
                            <TextArea
                                {...inputProps}
                                data-test-id='virtual-candidate-prompt'
                                value={choice.prompt}
                                height='200px'
                                onChange={(event) =>
                                    onChoiceChange({ ...choice, prompt: event.target.value })
                                }
                            />
                        )}
                    </InputWrapper>
                )}
            </Col>
        </ModalContent>
    )
}
